import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Check, X } from "lucide-react";

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

/**
 * Shown on every My Theatre tab while someone is waiting for an answer to a friend request.
 * One request: the name with Accept / Decline right in the banner. Several: "You have 4 friend requests" and a Review button
 * that opens a scrollable list. It keeps itself fresh (when the person returns to the tab, and every 45 seconds).
 */
export default function RequestsBanner() {
  const [incoming, setIncoming] = useState([]);
  const [busy, setBusy] = useState("");          // id of the request being answered
  const [open, setOpen] = useState(false);
  const live = useRef(true);

  const tell = (friends, list) => {
    try { window.dispatchEvent(new CustomEvent("mytheatre:friends", { detail: { friends, incoming: list } })); } catch { /* ignore */ }
  };
  const load = useCallback(async () => {
    try {
      const r = await api.get("/friends");
      if (!live.current) return;
      const list = r.data.has_username === false ? [] : r.data.incoming || [];
      setIncoming(list);
      tell((r.data.friends || []).length, list);
    } catch { /* keep what is on screen */ }
  }, []);

  useEffect(() => {
    live.current = true;
    load();
    const again = () => { if (document.visibilityState !== "hidden") load(); };
    const id = setInterval(again, 45000);
    const fromTab = (e) => { if (e.detail && Array.isArray(e.detail.incoming)) setIncoming(e.detail.incoming); };   // the Friends tab already has fresh data
    window.addEventListener("focus", again);
    document.addEventListener("visibilitychange", again);
    window.addEventListener("mytheatre:friends", fromTab);
    return () => {
      live.current = false; clearInterval(id);
      window.removeEventListener("focus", again); document.removeEventListener("visibilitychange", again); window.removeEventListener("mytheatre:friends", fromTab);
    };
  }, [load]);

  useEffect(() => { if (open && incoming.length === 0) setOpen(false); }, [open, incoming.length]);
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open]);

  const answer = async (r, accept) => {
    setBusy(r.id);
    try {
      await api.post(`/friends/${r.id}/respond`, { accept });
      toast.success(accept ? `You and ${r.username} are now friends` : "Request declined");
      setIncoming((l) => l.filter((x) => x.id !== r.id));
      await load();
      try { window.dispatchEvent(new Event("mytheatre:friends-changed")); } catch { /* ignore */ }
    } catch (e) { toast.error(errText(e, "Something went wrong. Please try again.")); }
    finally { setBusy(""); }
  };

  if (incoming.length === 0) return null;
  const one = incoming.length === 1 ? incoming[0] : null;

  const Buttons = ({ r, small }) => (
    <span className="flex gap-2">
      <button type="button" disabled={!!busy} data-testid={`accept-${r.username}`} onClick={() => answer(r, true)}
        className={`inline-flex items-center gap-1 rounded-full bg-[#C89B3C] font-semibold text-black disabled:opacity-60 ${small ? "px-3.5 py-1.5 text-sm" : "px-4 py-1.5 text-sm"}`}><Check className="h-4 w-4" /> Accept</button>
      <button type="button" disabled={!!busy} data-testid={`decline-${r.username}`} onClick={() => answer(r, false)}
        className="inline-flex items-center gap-1 rounded-full border border-border px-3.5 py-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-60"><X className="h-4 w-4" /> Decline</button>
    </span>
  );

  return (
    <>
      <section className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#C89B3C]/50 bg-gradient-to-r from-[#C89B3C]/15 to-transparent px-4 py-3" data-testid="requests-banner" role="status">
        <p className="flex min-w-0 items-center gap-2 text-sm">
          <UserPlus className="h-5 w-5 flex-shrink-0 text-[#C89B3C]" />
          {one
            ? <span className="min-w-0 break-words"><span className="font-semibold" data-testid="banner-name">{one.username}</span> wants to be your friend</span>
            : <span className="font-semibold" data-testid="banner-count">You have {incoming.length} friend requests</span>}
        </p>
        {one
          ? <Buttons r={one} />
          : <button type="button" onClick={() => setOpen(true)} data-testid="requests-review" className="rounded-full bg-[#C89B3C] px-5 py-1.5 text-sm font-semibold text-black">Review</button>}
      </section>

      {open && !one && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Friend requests" data-testid="requests-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="flex max-h-[85vh] w-full max-w-md flex-col rounded-t-2xl border border-border bg-card p-5 sm:rounded-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl font-semibold">Friend requests ({incoming.length})</h2>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="rounded-full p-1.5 hover:bg-secondary"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Friends can see your friends-only diary entries, lists and notes. Decline is private: they won't be told.</p>
            <ul className="mt-3 min-h-0 flex-1 space-y-2 overflow-y-auto pr-1" data-testid="requests-list">
              {incoming.map((r) => (
                <li key={r.id} data-testid="request-row" className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#C89B3C]/30 bg-[#C89B3C]/5 px-3 py-2.5">
                  <span className="min-w-0 break-words font-medium">{r.username}</span>
                  <Buttons r={r} small />
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}